import React, { useEffect, useState } from 'react';
import DBLogo from './assets/DBlogo.png'; // keep consistent with App.js
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button, Row, Col, Form as BootstrapForm, Dropdown, Modal } from 'react-bootstrap';
import { baseUrl } from './config/Config';
import { toast } from 'react-toastify';

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required('Required'),
  last_name: Yup.string().notRequired(),
  email: Yup.string().email('Invalid email').required('Required'),
  company: Yup.string().required('Required'),
  user_type: Yup.string().required('Required'),
  // allowed_sites is optional
});
const updateWholeDataValidationSchema = Yup.object().shape({
  first_name: Yup.string().required('Required'),
  last_name: Yup.string().notRequired(),
  username: Yup.string().email('Invalid email').required('Required'),
  company: Yup.string().required('Required'),
  user_type: Yup.string().required('Required'),
  subscription_start: Yup.string().required('Required'),
  subscription_end: Yup.string().required('Required'),
  // allowed_sites is optional
});

function AdminUI() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [contactUsUsers, setContactUsUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [createUserModal, setCreateUserModal] = useState(false);
  const [editingUserData, setEditingUserData] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [removeModal, setRemoveModal] = useState(false);
  const [deleteContactUsModal, setDeleteContactUsModal] = useState(false);
  const [deleteContactUsDetails, setDeleteContactUsDetails] = useState({});
  const [selectedremoveDomain, setRemoveDomain] = useState("");
  const [userUpdateId, setUserUpdateId] = useState(null);
  const [userUpdateModal, setUserUpdateModal] = useState(null);
  const [inlineUpdates,setInlineUpdates]=useState(null)
  const [inlineUpdateModal,setInlineUpdateModal]=useState(false)

  

  // Search + Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');     // all | admin | user
  const [filterStatus, setFilterStatus] = useState('all'); // all | active | pending | rejected

  // NEW: Email blocklist management state
  const [blocklist, setBlocklist] = useState([]); // array of domains
  const [blocklistAudit, setBlocklistAudit] = useState([]);
  const [domainInput, setDomainInput] = useState(''); // single add
  const [bulkDomainsText, setBulkDomainsText] = useState(''); // textarea (comma/line separated)
  const [blocklistSearch, setBlocklistSearch] = useState('');
  const [blLoading, setBlLoading] = useState(false);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };
  const currentUsername = localStorage.getItem('username') || '';

  // Domain helpers (same logic as App.js)
  const DOMAIN_REGEX = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
  function extractDomain(input) {
    if (!input) return '';
    const raw = String(input).trim().toLowerCase().replace(/^@/, '');
    try {
      const url = raw.includes('://') ? new URL(raw) : new URL(`http://${raw}`);
      return url.hostname.replace(/^www\./, '');
    } catch {
      return raw.replace(/^www\./, '');
    }
  }

  // helper: normalize any date-ish value to YYYY-MM-DD for <input type="date">
  const toISODate = (d) => {
    if (!d) return '';
    try {
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
      return dt.toISOString().slice(0, 10);
    } catch {
      return String(d).slice(0, 10);
    }
  };

  // --- Admin fetchers ---
  const fetchPendingUsers = async () => {
    try {
      const res = await fetch(`${baseUrl}/admin/pending-users`, { headers: authHeaders });
      if (!res.ok) {
        if (res.status === 403) return; // non-admins: ignore
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message||`Failed to fetch pending users`);
      }
      const data = await res.json();
      setPendingUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    }
  };
  // --- contactus list fetchers ---
  const fetchContactUsUsers = async () => {
    try {
      const res = await fetch(`${baseUrl}/admin/contactus`, { headers: authHeaders });
      if (!res.ok) {
        if (res.status === 403) return; // non-admins: ignore
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message||`Failed to fetch contactus users`);
      }
      const data = await res.json();
      setContactUsUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await fetch(`${baseUrl}/admin/users`, { headers: authHeaders });
      if (!res.ok) {
        if (res.status === 403) return;
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message||'Failed to fetch users');
      }
      const data = await res.json();
      setAllUsers(
        Array.isArray(data)
          ? data.map(u => ({
              ...u,
              sites_counted: u.sites_counted ?? 0,
              subscription_start: u.subscription_start,
              subscription_end: u.subscription_end,
              allowed_sites: u.allowed_sites ?? 50,
            }))
          : []
      );
    } catch (err) {
      setError(err.message);
    }
  };

  // const fetchMyUsage = async () => {
  //   try {
  //     const res = await fetch(`${baseUrl}/me/usage`, { headers: authHeaders });
  //     if (!res.ok) return;
  //     const data = await res.json();
  //     // setMyUsage(data?.site_count ?? 0);
  //   } catch {}
  // };

  // ---- NEW: Blocklist fetcher ----
  const fetchBlocklist = async () => {
    setBlLoading(true);
    try {
      const res = await fetch(`${baseUrl}/admin/email-blocklist`, { headers: authHeaders });
      if (!res.ok) {
        if (res.status === 403) return; // non-admins
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message||'Failed to fetch email blocklist');
        // throw new Error('Failed to fetch email blocklist');
      }
      const data = await res.json();
      setBlocklist(Array.isArray(data.domains) ? data.domains : []);
      setBlocklistAudit(Array.isArray(data.audit) ? data.audit : []);
    } catch (err) {
      setError(err.message);
    } finally {
      setBlLoading(false);
    }
  };

  const handleApproval = async (email, action) => {
    const url = `${baseUrl}/admin/${action}?email=${encodeURIComponent(email)}`;
    try {
      const res = await fetch(url, { method: 'POST', headers: authHeaders });
      if (!res.ok) {
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(`Failed to ${action.replace('-', ' ')} ${email}` + " "+errorData?.message);
      }
      await Promise.all([fetchPendingUsers(), fetchAllUsers()]);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleContacted = async (_id) => {
    const url = `${baseUrl}/admin/contactus/${_id}`;
    try {
      const res = await fetch(url, { method: 'PUT', headers: authHeaders });
      if (!res.ok) {
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message);
      }
      setUserUpdateModal(false)
      setUserUpdateId(null)
      await Promise.all([fetchContactUsUsers()]);
    } catch (err) {
      toast.error(err.message);
    }
  };
  const handleDeleteContactUs = async (data) => {
    const url = `${baseUrl}/admin/contactus/delete/${data._id}`;
    try {
      const res = await fetch(url, { method: 'DELETE', headers: authHeaders });
      if (!res.ok) {
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message);
      }
      setDeleteContactUsModal(false)
      setDeleteContactUsDetails(null)
      await Promise.all([fetchContactUsUsers()]);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateStatusOrPlan = async () => {
    // console.log("inlineUpdates",inlineUpdates)
    let payload={}
    if(inlineUpdates?.isPlan){
      payload={subscription_plan:inlineUpdates.changedValue}
    }else{
      payload={is_active:inlineUpdates.changedValue=="active"?true:false}
    }
    console.log("inlineUpdates",payload)

    const url = `${baseUrl}/admin/changeplanorstatus/${inlineUpdates.username}`;
    try {
      const res = await fetch(url, { method: 'PUT',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({...payload})
       });
      if (!res.ok) {
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message);
      }
      setInlineUpdateModal(false)
      setInlineUpdates(null)
      await Promise.all([fetchAllUsers()]);
    } catch (err) {
      toast.error(err.message);
    }
  };

  // ---- Unified update (dates + allowed_sites + user_type) ----
  // const buildUserUpdatePayload = (user) => {
  //   const buf = subscriptionUpdates[user.username] || {};

  //   // Default to current row values if not explicitly changed
  //   const start = buf.start ?? toISODate(user.subscription_start);
  //   const end = buf.end ?? toISODate(user.subscription_end);

  //   // allowed_sites: prefer typed number; fall back to existing
  //   const rawAllowed = buf.allowed_sites ?? user.allowed_sites ?? 50;
  //   const allowed_sites = String(rawAllowed).trim() === ''
  //     ? user.allowed_sites ?? 50
  //     : Number(rawAllowed);

  //   // user_type: when in edit mode, may be changed in-place
  //   const user_type = buf.user_type ?? user.user_type ?? 'user';

  //   return { start, end, allowed_sites, user_type };
  // };

  // const handleUpdateAll = async (user) => {
  //   const payload = buildUserUpdatePayload(user);

  //   // UI guard: don't let me demote myself
  //   if (user.username === currentUsername && payload.user_type && payload.user_type !== 'admin') {
  //     toast.error("You cannot change your own role to 'Normal User'.");
  //     return;
  //   }

  //   // quick guards
  //   if (!payload.start || !payload.end) {
  //     return toast.error('Both start and end dates are required');
  //   }
  //   if (Number.isNaN(Number(payload.allowed_sites)) || Number(payload.allowed_sites) < 0) {
  //     return toast.error('Allowed sites must be a non-negative number');
  //   }

  //   try {
  //     const res = await fetch(`${baseUrl}/admin/update/${encodeURIComponent(user.username)}`, {
  //       method: 'POST',
  //       headers: { ...authHeaders, 'Content-Type': 'application/json' },
  //       body: JSON.stringify(payload)
  //     });
  //     if (!res.ok) {
  //       const errorData = await res.json();
  //       // console.log(errorData.message); 
  //       throw new Error(errorData?.message||'Failed to update users');
  //       // const txt = await res.text().catch(() => '');
  //       // throw new Error(txt || 'Failed to update user');
  //     }
  //     setEditingUserId(null);
  //     setSubscriptionUpdates(prev => ({ ...prev, [user.username]: {} }));
  //     await fetchAllUsers();
  //   } catch (err) {
  //     toast.error(err.message);
  //   }
  // };

  // “Update” button when not in editing mode (dates + allowed sites only)
  // const handleSubscriptionUpdate = async (username, userRow) => {
  //   const buf = subscriptionUpdates[username] || {};
  //   const start = buf.start ?? toISODate(userRow.subscription_start);
  //   const end = buf.end ?? toISODate(userRow.subscription_end);
  //   const rawAllowed = buf.allowed_sites ?? userRow.allowed_sites ?? 50;
  //   const allowed_sites = String(rawAllowed).trim() === ''
  //     ? userRow.allowed_sites ?? 50
  //     : Number(rawAllowed);

  //   if (!start || !end) return toast.error('Both start and end dates are required');
  //   if (Number.isNaN(Number(allowed_sites)) || Number(allowed_sites) < 0) {
  //     return toast.error('Allowed sites must be a non-negative number');
  //   }

  //   try {
  //     const res = await fetch(`${baseUrl}/admin/update/${encodeURIComponent(username)}`, {
  //       method: 'POST',
  //       headers: { ...authHeaders, 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ start, end, allowed_sites })
  //     });
  //     if (!res.ok) {
  //       const errorData = await res.json();
  //       // console.log(errorData.message); 
  //       throw new Error(errorData?.message||'Failed to update subscription');
  //       // const txt = await res.text().catch(() => '');
  //       // throw new Error(txt || 'Failed to update subscription');
  //     }
  //     await fetchAllUsers();
  //   } catch (err) {
  //     toast.error(err.message);
  //   }
  // };

  const handleEditClick = (user) => {
    setEditingUserData({ ...user });
    setShowModal(true);
  };

  // const updateEditingField = (field, value) => {
  //   setEditingUserData(prev => ({ ...prev, [field]: value }));
  // };

  // const handleModalSave = () => {
  //   // Send updated data to API
  //   updateUser(editingUserData); // <- Your update handler
  //   setShowModal(false);
  // };

  const handleDeleteClick = (user) => {
    setEditingUserData({...user});
    setDeleteModal(true);
  };

  // Create user
  // const handleNewUser = async () => {
  //   const { first_name, last_name, email, company, user_type, allowed_sites } = newUser;
  //   if (!first_name || !last_name || !email || !company || !user_type) {
  //     return toast.error('Fill all fields');
  //   }

  //   // Normalize + validate company domain
  //   const companyDomain = extractDomain(company);
  //   if (!companyDomain || !DOMAIN_REGEX.test(companyDomain)) {
  //     return toast.error('Please enter a valid company domain (e.g. example.com)');
  //   }

  //   const payload = { first_name, last_name, email, company: companyDomain, user_type };
  //   if (allowed_sites !== '') payload.allowed_sites = Number(allowed_sites);

  //   try {
  //     const res = await fetch(`${baseUrl}/admin/create`, {
  //       method: 'POST',
  //       headers: { ...authHeaders, 'Content-Type': 'application/json' },
  //       body: JSON.stringify(payload)
  //     });
  //     if (!res.ok) {
  //       const txt = await res.text().catch(() => '');
  //       throw new Error(txt || 'Failed to create user');
  //     }
  //     setNewUser({ first_name: '', last_name: '', email: '', company: '', user_type: '', allowed_sites: '' });
  //     await fetchAllUsers();
  //   } catch (err) {
  //     toast.error(err.message);
  //   }
  // };

  const handleNewUser = async (values,resetForm=()=>{}) => {
    const { first_name, last_name, email, company, user_type, allowed_sites } = values;
    if (!first_name || !last_name || !email || !company || !user_type) {
      return toast.error('Fill all fields');
    }

    // Normalize + validate company domain
    const companyDomain = extractDomain(company);
    if (!companyDomain || !DOMAIN_REGEX.test(companyDomain)) {
      return toast.error('Please enter a valid company domain (e.g. example.com)');
    }

    const payload = { first_name, last_name, email, company: companyDomain, user_type };
    if (allowed_sites !== '') payload.allowed_sites = Number(allowed_sites);

    try {
      const res = await fetch(`${baseUrl}/admin/create`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message||'Failed to create user');
        // const txt = await res.text().catch(() => '');
        // throw new Error(txt || 'Failed to create user');
      }
      // setNewUser({ first_name: '', last_name: '', email: '', company: '', user_type: '', allowed_sites: '' });
      await fetchAllUsers();
      setCreateUserModal(false)
      resetForm();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateUser = async (values,resetForm=()=>{}) => {
    const { first_name, last_name,username, company, user_type, allowed_sites,subscription_start, subscription_end } = values;
    if (!first_name || !last_name || !company || !user_type) {
      return toast.error('Fill all fields');
    }

    // Normalize + validate company domain
    const companyDomain = extractDomain(company);
    if (!companyDomain || !DOMAIN_REGEX.test(companyDomain)) {
      return toast.error('Please enter a valid company domain (e.g. example.com)');
    }

    const payload = { first_name, last_name,  company: companyDomain, user_type };

    const start = subscription_start ? toISODate(subscription_start):"";
    const end = subscription_end ? toISODate(subscription_end):"";
    const rawAllowed = allowed_sites ?? 50;
    const _allowed_sites = String(rawAllowed).trim() === ''
      ? userRow.allowed_sites ?? 50
      : Number(rawAllowed);

    if (!start || !end) return toast.error('Both start and end dates are required');
    if (Number.isNaN(Number(allowed_sites)) || Number(allowed_sites) < 0) {
      return toast.error('Allowed sites must be a non-negative number');
    }

    try {
      const res = await fetch(`${baseUrl}/admin/update/${encodeURIComponent(username)}`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ start, end, allowed_sites:_allowed_sites, ...payload })
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData?.message||'Failed to update user/subscription');
      }
      resetForm();
      setEditingUserData(null);
      setShowModal(false)
      await fetchAllUsers();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (user) => {
    try {
      const res = await fetch(`${baseUrl}/admin/delete/${user?.user_id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) {
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message||'Failed to delete user');
        // throw new Error('Failed to delete user');
      }
      await fetchAllUsers();
      setDeleteModal(false);
      setEditingUserData(null)
    } catch (err) {
      toast.error(err.message);
    }
  };

  // const handleEdit = () => {
  //   if (!selectedUser) return toast.error('Select a user');
  //   setEditingUserId(selectedUser);
  // };

  // keep local row edits (user_type, names/company if you want)
  // const handleUserChange = (userId, key, value) => {
  //   setAllUsers(prev =>
  //     prev.map(user => (user.user_id === userId ? { ...user, [key]: value } : user))
  //   );
  //   // Track in subscriptionUpdates for fields we ship to the backend
  //   const user = allUsers.find(u => u.user_id === userId);
  //   if (user) {
  //     setSubscriptionUpdates(prev => ({
  //       ...prev,
  //       [user.username]: {
  //         ...prev[user.username],
  //         [key]: value
  //       }
  //     }));
  //   }
  // };

  // ---- NEW: helpers for parsing bulk domain input ----
  const parseDomains = (text) => {
    return (text || '')
      .split(/\n|,|;|\s+/)
      .map(s => s.trim().toLowerCase().replace(/^@/, ''))
      .filter(s => s && s.includes('.'));
  };

  const addSingleDomain = async () => {
    const doms = parseDomains(domainInput);
    if (doms.length === 0) return toast.error('Enter a valid domain');
    try {
      const res = await fetch(`${baseUrl}/admin/email-blocklist/add`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ domains: doms })
      });
      if (!res.ok) {
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message||'Failed to add domain');
        // const txt = await res.text().catch(() => '');
        // throw new Error(txt || 'Failed to add domain');
      }
      setDomainInput('');
      await Promise.all([fetchBlocklist(), fetchAllUsers(), fetchPendingUsers()]);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const addBulkDomains = async () => {
    const doms = parseDomains(bulkDomainsText);
    if (doms.length === 0) return toast.error('Paste one or more domains (comma or newline separated)');
    try {
      const res = await fetch(`${baseUrl}/admin/email-blocklist/bulk`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ domains: doms })
      });
      if (!res.ok) {
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message||'Failed to add domain');
        // const txt = await res.text().catch(() => '');
        // throw new Error(txt || 'Failed to add domains');
      }
      setBulkDomainsText('');
      await Promise.all([fetchBlocklist(), fetchAllUsers(), fetchPendingUsers()]);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const removeDomain = async (domain) => {
    try {
      const res = await fetch(`${baseUrl}/admin/email-blocklist/${encodeURIComponent(domain)}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) {
        const errorData = await res.json();
        // console.log(errorData.message); 
        throw new Error(errorData?.message||'Failed to remove domain');
        // const txt = await res.text().catch(() => '');
        // throw new Error(txt || 'Failed to remove domain');
      }
      await fetchBlocklist();
      setRemoveModal(false);
      setRemoveDomain(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchPendingUsers(), fetchAllUsers(),  fetchBlocklist(),fetchContactUsUsers()])
      .finally(() => setLoading(false));
  }, []);

  // ---------- FILTERING / SEARCH ----------
  const normalized = (val) => (val ?? '').toString().toLowerCase();
  const filteredUsers = allUsers.filter(u => {
    const s = normalized(searchTerm);
    const matchesSearch =
      !s ||
      normalized(u.username).includes(s) ||
      normalized(u.first_name).includes(s) ||
      normalized(u.last_name).includes(s) ||
      normalized(u.email || u.username).includes(s) ||
      normalized(u.company).includes(s);

    const matchesType =
      filterType === 'all' || normalized(u.user_type) === filterType;

    const matchesStatus =
      filterStatus === 'all' || normalized(u.status) === filterStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  // console.log("filteredUsers",filteredUsers)
  
  const filteredBlocklist = blocklist.filter(d => normalized(d).includes(normalized(blocklistSearch)));
  if (loading) return <p>Loading admin UI...</p>;
  if (error) return <p style={{ color: 'red' }}>{error}</p>;

  return (
    <>
      <div className="stack mx-3">

        {/* Pending approvals */}
        <div className="card">
          <h5 className="app-title" style={{ marginTop: 0 }}>Pending User Approvals</h5>
          {pendingUsers.length === 0 ? (
            <p className="caption">No pending users found.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Full Name</th>
                    {/* <th>Last Name</th> */}
                    <th>Email</th>
                    <th>Company Domain</th>
                    {/* <th>User Type</th> */}
                    <th>Email Verified</th>
                    {/* <th>Status</th>
                    <th>Start Date</th>
                    <th>End Date</th> */}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingUsers.map((user, i) => (
                    <tr key={i}>
                      <td>{user.full_name}</td>
                      {/* <td>{user.last_name}</td> */}
                      <td>{user.email}</td>
                      <td>{user.org_name}</td>
                      {/* <td>{user.role}</td> */}
                      <td>
                        {user.email_verified ? (
                          <span className="success" style={{ display: 'inline-block', margin: 0, padding: '4px 8px' }}>
                            Verified
                          </span>
                        ) : (
                          <span className="caption" style={{ display: 'inline-block', padding: '4px 8px' }}>
                            Not Verified
                          </span>
                        )}
                      </td>
                      {/* <td>{user.status}</td>
                      <td>{toISODate(user.subscription_start)}</td>
                      <td>{toISODate(user.subscription_end)}</td> */}
                      <td>
                        <div className="d-flex gap-2">
                          <button className="btn btn-primary" onClick={() => handleApproval(user.email, 'approve-user')}>Approve</button>
                          <button className="btn btn-danger" onClick={() => handleApproval(user.email, 'reject-user')}>Reject</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Contact us list */}
        <div className="card">
          <h5 className="app-title" style={{ marginTop: 0 }}>Contacted Users</h5>
          {contactUsUsers.length === 0 ? (
            <p className="caption">No users found.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Contact Number</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {contactUsUsers.map((user, i) => (
                    <tr key={i}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.contact_number}</td>
                      <td>
                        <div className="d-flex gap-2">
                          {/* <button className="btn btn-primary" onClick={() => {setUserUpdateId(user._id)
                            setUserUpdateModal(true)
                          }}>Update Status</button> */}
                          <button className="btn btn-danger" onClick={() => {setDeleteContactUsDetails(user);setDeleteContactUsModal(true);}}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add New User — directly after Pending Approvals */}
        {/* <div className="card">
          <h5 className="app-title mb-3">Add New User</h5>
          <div>
            <Formik
              initialValues={{
                first_name: '',
                last_name: '',
                email: '',
                company: '',
                user_type: '',
                allowed_sites: ''
              }}
              validationSchema={validationSchema}
              onSubmit={(values, {resetForm}) => {
                values.company = extractDomain(values.company);
                handleNewUser(values,resetForm);
                // actions.setSubmitting(false);
                
              }}
            >
              {({ values, handleChange, handleBlur, touched, errors }) => (
                <Form>
                  <Row className="mb-3">
                    <Col md={6} lg={4}>
                      <BootstrapForm.Control
                        type="text"
                        name="first_name"
                        placeholder="First Name"
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.first_name && !!errors.first_name}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.first_name}
                      </BootstrapForm.Control.Feedback>
                    </Col>

                    <Col md={6} lg={4}>
                      <BootstrapForm.Control
                        type="text"
                        name="last_name"
                        placeholder="Last Name"
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.last_name && !!errors.last_name}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.last_name}
                      </BootstrapForm.Control.Feedback>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6} lg={4}>
                      <BootstrapForm.Control
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.email}
                      </BootstrapForm.Control.Feedback>
                    </Col>

                    <Col md={6} lg={4}>
                      <BootstrapForm.Control
                        type="text"
                        name="company"
                        placeholder="Company domain (e.g. example.com)"
                        value={values.company}
                        onChange={handleChange}
                        onBlur={(e) => {
                          handleBlur(e);
                          values.company = extractDomain(values.company);
                        }}
                        isInvalid={touched.company && !!errors.company}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.company}
                      </BootstrapForm.Control.Feedback>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6} lg={4}>
                      <BootstrapForm.Select
                        name="user_type"
                        value={values.user_type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.user_type && !!errors.user_type}
                      >
                        <option value="">Select User Type</option>
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                      </BootstrapForm.Select>
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.user_type}
                      </BootstrapForm.Control.Feedback>
                    </Col>

                    <Col md={6} lg={4}>
                      <BootstrapForm.Control
                        type="number"
                        name="allowed_sites"
                        placeholder="Allowed Sites (optional, default 50)"
                        min="0"
                        value={values.allowed_sites}
                        onChange={handleChange}
                      />
                    </Col>
                  </Row>
                  <div style={{width:"fit-content"}}>
                      <Button type="submit" variant="primary" style={{ width: '100%' }}>
                        Create User
                      </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div> */}

        {/* Existing users */}
        <div className="card">
          <div className="d-flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 className="app-title" style={{ margin: 0 }}>Existing Users</h5>
            <button className='btn btn-primary' onClick={()=>{setCreateUserModal(true)}}>Add New User</button>
          </div>

          {/* Search + Filters (compact with icons) */}
          <div className="d-flex align-items-baseline" style={{ gap: 12, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="d-flex" style={{ alignItems: 'center', gap: 6 }}>
              <span aria-hidden="true" style={{ fontSize: 18, opacity: 0.7 }}>🔎</span>
              <input
                className="input"
                style={{ maxWidth: 320 }}
                placeholder="Search name, username, email, company domain"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="d-flex" style={{ alignItems: 'center', gap: 6 }}>
              <span aria-hidden="true" style={{ fontSize: 18, opacity: 0.7 }}>🧰</span>
              <select
                className="select"
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
                title="Filter by user type"
              >
                <option value="all">All Types</option>
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </div>

            <div className="d-flex" style={{ alignItems: 'center', gap: 6 }}>
              <span aria-hidden="true" style={{ fontSize: 18, opacity: 0.7 }}>🧭</span>
              <select
                className="select"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                title="Filter by status"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>

            <button
              className="btn btn-warning"
              onClick={() => { setSearchTerm(''); setFilterType('all'); setFilterStatus('all'); }}
              title="Clear filters"
            >
              ✨ Clear
            </button>
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {/* <th>Select</th> */}
                  <th>User ID</th>
                  <th>Email (username)</th>
                  {/* <th>Username</th> */}
                  <th>Full Name</th>
                  {/* <th>Last Name</th> */}
                  <th>Company Domain</th>
                  <th>User Type</th>
                  <th>subscription Plan</th>
                  <th>Status</th>
                  <th>Sites Counted</th>
                  <th>Allowed Sites</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, i) => (
                  <tr key={i}>
                    <td>{user.user_id}</td>
                    <td>{user.username}</td>
                    <td>{user.first_name} {user.last_name}</td>
                    {/* <td>{user.last_name}</td> */}
                    <td>{user.company}</td>
                    {/* <td>
                      {editingUserId === user.user_id ? (
                        <input
                          className="input"
                          value={user.first_name || ''}
                          onChange={e => handleUserChange(user.user_id, 'first_name', e.target.value)}
                        />
                      ) : (
                        user.first_name
                      )}
                    </td> */}
                    {/* <td>
                      {editingUserId === user.user_id ? (
                        <input
                          className="input"
                          value={user.last_name || ''}
                          onChange={e => handleUserChange(user.user_id, 'last_name', e.target.value)}
                        />
                      ) : (
                        user.last_name
                      )}
                    </td> */}
                    {/* <td>
                      {editingUserId === user.user_id ? (
                        <input
                          className="input"
                          value={user.company || ''}
                          onChange={e => handleUserChange(user.user_id, 'company', e.target.value)}
                        />
                      ) : (
                        user.company
                      )}
                    </td> */}
                    {/* <td>
                      {editingUserId === user.user_id ? (
                        <select
                          className="select"
                          value={
                            subscriptionUpdates[user.username]?.user_type ??
                            user.user_type ?? 'user'
                          }
                          onChange={e => handleUserChange(user.user_id, 'user_type', e.target.value)}
                          disabled={user.username === currentUsername}
                          title={user.username === currentUsername ? 'You cannot change your own role' : 'Change user role'}
                        >
                          <option value="admin">Admin</option>
                          <option value="user">User</option>
                        </select>
                      ) : (
                        user.user_type
                      )}
                    </td> */}
                    <td>{user.user_type}</td>
                    {/* <td>{user.subscription_plan}</td> */}
                    <td>
                      <select className="input" value={user.subscription_plan}
                      onChange={e =>{
                          setInlineUpdates({...user,isPlan:true,changedValue:e.target.value})
                          setInlineUpdateModal(true)
                        }
                        }>
                        <option value={"free"}>Free</option>
                        <option value={"paid"}>Paid</option>
                      </select>
                    </td>
                    <td>
                      <select style={{width:"90px", paddingLeft:"5px",paddingRight:"5px"}} className="input" value={user.status}
                      onChange={e =>{
                        setInlineUpdates({...user,isPlan:false,changedValue:e.target.value})
                          setInlineUpdateModal(true)
                        }}>
                        <option value={"active"}>Active</option>
                        <option value={"inactive"}>Inactive</option>
                      </select>
                    </td>
                    {/* <td>{user.status}</td> */}
                    <td>{user.sites_counted ?? 0}</td>
                    <td>{user.allowed_sites ?? 50}</td>
                    <td className='no-wrap'>{user.subscription_start}</td>
                    <td className='no-wrap'>{user.subscription_end}</td>
                    {/* Menu: Edit/Delete */}
                    <td className='text-center'>
                      <div className='d-flex gap-2'>
                        <button
                          className="btn btn-primary"
                          onClick={() => handleEditClick(user)}
                          title="Update data"
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-danger"
                          onClick={() => handleDeleteClick(user)}
                          title="delete"
                        >
                          Delete
                        </button>
                      </div>
                      {/* <Dropdown className='mx-auto'>
                        <Dropdown.Toggle variant="light" size="sm" style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          fontSize: '1.5rem',
                          lineHeight: '1',
                          boxShadow: 'none',
                        }}
                        className="no-caret" id="dropdown-basic">
                          ⋮
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => handleEditClick(user)}>Edit</Dropdown.Item>
                          <Dropdown.Item onClick={() => handleDeleteClick(user)}>Delete</Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown> */}
                    </td>

                    {/* Allowed Sites (quota) input */}
                    {/* <td>
                      <input
                        className="input"
                        type="number"
                        min="0"
                        value={
                          subscriptionUpdates[user.username]?.allowed_sites ??
                          (user.allowed_sites ?? 50)
                        }
                        onChange={e =>
                          setSubscriptionUpdates(prev => ({
                            ...prev,
                            [user.username]: {
                              ...prev[user.username],
                              allowed_sites: e.target.value
                            }
                          }))
                        }
                        style={{ maxWidth: 140 }}
                      />
                    </td> */}

                    {/* Start Date */}
                    {/* <td>
                      <input
                        className="input"
                        type="date"
                        value={subscriptionUpdates[user.username]?.start ?? toISODate(user.subscription_start)}
                        onChange={e =>
                          setSubscriptionUpdates(prev => ({
                            ...prev,
                            [user.username]: { ...prev[user.username], start: e.target.value }
                          }))
                        }
                      />
                    </td> */}

                    {/* End Date */}
                    {/* <td>
                      <input
                        className="input"
                        type="date"
                        value={subscriptionUpdates[user.username]?.end ?? toISODate(user.subscription_end)}
                        onChange={e =>
                          setSubscriptionUpdates(prev => ({
                            ...prev,
                            [user.username]: { ...prev[user.username], end: e.target.value }
                          }))
                        }
                      />
                    </td> */}
                    

                    {/* <td>
                      {editingUserId === user.user_id ? (
                        <button
                          className="btn btn-primary"
                          onClick={() => handleUpdateAll(user)}
                          title="Save type, dates and allowed sites"
                        >
                          Save
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary"
                          onClick={() => handleSubscriptionUpdate(user.username, user)}
                          title="Update dates and allowed sites"
                        >
                          Update
                        </button>
                      )}
                    </td> */}
                  </tr>
                ))}

                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan="14" className="caption">No users match your search/filters.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* NEW: Disposable Email Domains Blocklist */}
        <div className="card">
          <div className="d-flex" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <h5 className="app-title" style={{ margin: 0 }}>Disposable Email Domains Blocklist</h5>
            <strong className="caption">{blLoading ? 'Refreshing…' : `Total: ${blocklist.length}`}</strong>
          </div>

          {/* Add single domain */}
          <div className="form-row" style={{ gap: 8, marginTop: 12 }}>
            <input
              className="input"
              style={{ maxWidth: 360 }}
              placeholder="Add a domain e.g. mailinator.com or yopmail.com"
              value={domainInput}
              onChange={e => setDomainInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addSingleDomain(); }}
            />
            <button className="btn btn-primary" style={{ padding: '6px 12px' }} onClick={addSingleDomain}>
              Add
            </button>
            <button className="btn btn-primary" style={{ padding: '6px 12px' }} onClick={fetchBlocklist}>
              ↻ Refresh
            </button>
          </div>

          {/* Bulk add */}
          <div className="form-row" style={{ gap: 12, marginTop: 12 }}>
            <textarea
              className="input"
              rows={3}
              placeholder={'Bulk add (comma, space or newline separated)\nExample:\nmailinator.com\nyopmail.com, 10minutemail.com'}
              value={bulkDomainsText}
              onChange={e => setBulkDomainsText(e.target.value)}
              style={{ width: '100%', maxWidth: 640 }}
            />
            <button className="btn btn-primary" onClick={addBulkDomains}>Bulk Add</button>
          </div>

          {/* Search within blocklist */}
          <div className="d-flex" style={{ gap: 12, marginTop: 12, alignItems: 'center' }}>
            <span aria-hidden="true" style={{ fontSize: 18, opacity: 0.7 }}>🔎</span>
            <input
              className="input"
              placeholder="Search domain in blocklist"
              value={blocklistSearch}
              onChange={e => setBlocklistSearch(e.target.value)}
              style={{ maxWidth: 320 }}
            />
          </div>

          {/* Blocklist table */}
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: '60%' }}>Domain</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredBlocklist.map((d, i) => (
                  <tr key={i}>
                    <td>{d}</td>
                    <td>
                      <button className="btn btn-link" onClick={() => {setRemoveDomain(d)
                        setRemoveModal(true)
                      }} title="Remove domain">Remove</button>
                    </td>
                  </tr>
                ))}
                {filteredBlocklist.length === 0 && (
                  <tr>
                    <td className="caption" colSpan={2}>No domains found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Simple Audit tail */}
          <div style={{ marginTop: 12 }}>
            <details>
              <summary className="caption">Recent actions (last 50)</summary>
              <ul className="caption" style={{ marginTop: 8 }}>
                {blocklistAudit.map((a, idx) => (
                  <li key={idx}>{a.at} — <strong>{a.action}</strong> {a.domain} by {a.by}</li>
                ))}
                {blocklistAudit.length === 0 && <li>None yet</li>}
              </ul>
            </details>
          </div>
        </div>

        <div className='mb-2' style={{ fontSize: 14, marginTop: 6, color: '#003bc0',textAlign: 'center' }}>
            Powered by <strong>Dollarbird Technologies Pvt Ltd</strong>
          </div>
      </div>

      <Modal size='lg' show={createUserModal} onHide={() => {setCreateUserModal(false);}}>
        <Modal.Header closeButton style={{background:"linear-gradient(180deg, var(--muted), #f7fbff)"}}>
          <Modal.Title>Add New User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
              initialValues={{
                first_name: '',
                last_name: '',
                email: '',
                company: '',
                user_type: '',
                allowed_sites: ''
              }}
              validationSchema={validationSchema}
              onSubmit={(values, {resetForm}) => {
                values.company = extractDomain(values.company);
                handleNewUser(values,resetForm);
              }}
            >
              {({ values, handleChange, handleBlur, touched, errors }) => (
                <Form>
                  {/* First & Last Name */}
                  <Row className="mb-3">
                    <Col md={6}>
                      <BootstrapForm.Control
                        type="text"
                        name="first_name"
                        placeholder="First Name"
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.first_name && !!errors.first_name}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.first_name}
                      </BootstrapForm.Control.Feedback>
                    </Col>

                    <Col md={6}>
                      <BootstrapForm.Control
                        type="text"
                        name="last_name"
                        placeholder="Last Name"
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.last_name && !!errors.last_name}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.last_name}
                      </BootstrapForm.Control.Feedback>
                    </Col>
                  </Row>

                  {/* Email & Company Domain */}
                  <Row className="mb-3">
                    <Col md={6}>
                      <BootstrapForm.Control
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.email}
                      </BootstrapForm.Control.Feedback>
                    </Col>

                    <Col md={6}>
                      <BootstrapForm.Control
                        type="text"
                        name="company"
                        placeholder="Company domain (e.g. example.com)"
                        value={values.company}
                        onChange={handleChange}
                        onBlur={(e) => {
                          handleBlur(e);
                          values.company = extractDomain(values.company);
                        }}
                        isInvalid={touched.company && !!errors.company}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.company}
                      </BootstrapForm.Control.Feedback>
                    </Col>
                  </Row>

                  {/* User Type, Allowed Sites, Button */}
                  <Row className="mb-3">
                    <Col md={6}>
                      <BootstrapForm.Select
                        name="user_type"
                        value={values.user_type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.user_type && !!errors.user_type}
                      >
                        <option value="">Select User Type</option>
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                      </BootstrapForm.Select>
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.user_type}
                      </BootstrapForm.Control.Feedback>
                    </Col>

                    <Col md={6}>
                      <BootstrapForm.Control
                        type="number"
                        name="allowed_sites"
                        placeholder="Allowed Sites (optional, default 50)"
                        min="0"
                        value={values.allowed_sites}
                        onChange={handleChange}
                      />
                      {/* No validation for this field */}
                    </Col>
                  </Row>
                  <div className='d-flex justify-content-end gap-3 mt-4'>
                      <Button type="button" variant="secondary" onClick={()=>setCreateUserModal(false)}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="primary">
                        Create User
                      </Button>
                  </div>
                </Form>
              )}
            </Formik>
        </Modal.Body>
      </Modal>

      <Modal size='lg' show={showModal} onHide={() => {setShowModal(false); setEditingUserData(null);}}>
        <Modal.Header closeButton style={{background:"linear-gradient(180deg, var(--muted), #f7fbff)"}}>
          <Modal.Title>Update User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
         <Formik
            enableReinitialize
              initialValues={{
                first_name: editingUserData?.first_name || '',
                last_name: editingUserData?.last_name || '',
                username: editingUserData?.username || '',
                company: editingUserData?.company || '',
                user_type: editingUserData?.user_type || 'user',
                allowed_sites: editingUserData?.allowed_sites ?? 50,
                subscription_start:editingUserData?.subscription_start? toISODate(editingUserData?.subscription_start) : '',
                subscription_end:editingUserData?.subscription_end? toISODate(editingUserData?.subscription_end) : '',
              }}
              validationSchema={updateWholeDataValidationSchema}
              onSubmit={(values, {resetForm}) => {
                // values.company = extractDomain(values.company);
                handleUpdateUser(values,resetForm);
                // actions.setSubmitting(false);
                
              }}
            >
              {({ values, handleChange, handleBlur, touched, errors }) => (
                <Form>
                  {/* First & Last Name */}
                  <Row className="gap-0 mb-3">
                    <Col md={6}>
                    <label className='mb-1'><strong>First Name</strong></label>
                      <BootstrapForm.Control
                        type="text"
                        name="first_name"
                        placeholder="First Name"
                        value={values.first_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.first_name && !!errors.first_name}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.first_name}
                      </BootstrapForm.Control.Feedback>
                    </Col>

                    <Col md={6}>
                    <label className='mb-1'><strong>Last Name</strong></label>
                      <BootstrapForm.Control
                        type="text"
                        name="last_name"
                        placeholder="Last Name"
                        value={values.last_name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.last_name && !!errors.last_name}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.last_name}
                      </BootstrapForm.Control.Feedback>
                    </Col>
                  </Row>

                  {/* Email & Company Domain */}
                  <Row className="gap-0 mb-3">
                    <Col md={6}>
                    <label className='mb-1'><strong>Username(Email)</strong></label>
                      <BootstrapForm.Control
                        type="email"
                        name="username"
                        placeholder="username"
                        value={values.username}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.username && !!errors.username}
                        disabled={true}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.username}
                      </BootstrapForm.Control.Feedback>
                    </Col>

                    <Col md={6}>
                    <label className='mb-1'><strong>Company Domain</strong></label>
                      <BootstrapForm.Control
                        type="text"
                        name="company"
                        placeholder="Company domain (e.g. example.com)"
                        value={values.company}
                        onChange={handleChange}
                        onBlur={(e) => {
                          handleBlur(e);
                          values.company = extractDomain(values.company);
                        }}
                        isInvalid={touched.company && !!errors.company}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.company}
                      </BootstrapForm.Control.Feedback>
                    </Col>
                  </Row>

                  {/* User Type, Allowed Sites, Button */}
                  <Row className="gap-0 mb-3">
                    <Col md={6}>
                    <label className='mb-1'><strong>User type</strong></label>
                      <BootstrapForm.Select
                        name="user_type"
                        value={values.user_type}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.user_type && !!errors.user_type}
                      >
                        <option value="">Select User Type</option>
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                      </BootstrapForm.Select>
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.user_type}
                      </BootstrapForm.Control.Feedback>
                    </Col>

                    <Col md={6}>
                    <label className='mb-1'><strong>Allowed sites</strong></label>
                      <BootstrapForm.Control
                        type="number"
                        name="allowed_sites"
                        placeholder="Allowed Sites (optional, default 50)"
                        min="0"
                        value={values.allowed_sites}
                        onChange={handleChange}
                      />
                      {/* No validation for this field */}
                    </Col>
                  </Row>
                  <Row className="gap-0 mb-3">
                    <Col md={6}>
                    <label className='mb-1'><strong>Start Date</strong></label>
                       <BootstrapForm.Control
                        type="date"
                        name="subscription_start"
                        placeholder="start date"
                        value={values.subscription_start}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.subscription_start && !!errors.subscription_start}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.subscription_start}
                      </BootstrapForm.Control.Feedback>
                    </Col>

                    <Col md={6}>
                    <label className='mb-1'><strong>End Date</strong></label>
                       <BootstrapForm.Control
                        type="date"
                        name="subscription_end"
                        placeholder="start date"
                        value={values.subscription_end}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.subscription_end && !!errors.subscription_end}
                      />
                      <BootstrapForm.Control.Feedback type="invalid">
                        {errors.subscription_end}
                      </BootstrapForm.Control.Feedback>
                    </Col>
                  </Row>
                  <div className='d-flex gap-3 mt-2 justify-content-end'>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </Button>
                      <Button type="submit" variant="primary">
                        Update
                      </Button>
                  </div>
                </Form>
              )}
            </Formik>
        </Modal.Body>
      </Modal>

      <Modal size='md' show={deleteModal} onHide={() => {setDeleteModal(false); setEditingUserData(null);}}>
        <Modal.Header closeButton style={{background:"linear-gradient(180deg, var(--muted), #f7fbff)"}}>
          <Modal.Title>Delete User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
              <h5>Are you sure you want to delete this user?</h5>

              <div className='d-flex gap-3 mt-3 justify-content-end'>
                    <Button variant="secondary" onClick={() => setDeleteModal(false)}>
                      Cancel
                    </Button>
                      <Button type="button" variant="danger" onClick={() => handleDelete(editingUserData)}>
                        Delete
                      </Button>
                  </div>
        </Modal.Body>
      </Modal>

      <Modal size='md' show={userUpdateModal} onHide={() => {setUserUpdateModal(false); setUserUpdateId(null);}}>
        <Modal.Header closeButton style={{background:"linear-gradient(180deg, var(--muted), #f7fbff)"}}>
          <Modal.Title>Update Contact status</Modal.Title>
        </Modal.Header>
        <Modal.Body>
              <h5>Have you contacted the User?</h5>

              <div className='d-flex gap-3 mt-4 justify-content-end'>
                    <Button variant="secondary" onClick={() => setUserUpdateModal(false)}>
                      Cancel
                    </Button>
                      <Button type="button" variant="primary" onClick={() => handleContacted(userUpdateId)}>
                        Update Status
                      </Button>
                  </div>
        </Modal.Body>
      </Modal>

      <Modal size='md' show={removeModal} onHide={() => {setRemoveModal(false); setRemoveDomain(null);}}>
        <Modal.Header closeButton style={{background:"linear-gradient(180deg, var(--muted), #f7fbff)"}}>
          <Modal.Title>Remove Domain</Modal.Title>
        </Modal.Header>
        <Modal.Body>
              <p style={{fontSize:18}}>Are you sure you want to remove <strong>{selectedremoveDomain}</strong> from blocklist?</p>

              <div className='d-flex gap-3 mt-3 justify-content-end'>
                    <Button variant="secondary" onClick={() => setRemoveModal(false)}>
                      Cancel
                    </Button>
                      <Button type="button" variant="danger" onClick={() => removeDomain(selectedremoveDomain)}>
                        Remove
                      </Button>
                  </div>
        </Modal.Body>
      </Modal>

      <Modal size='md' show={inlineUpdateModal} onHide={() => {setInlineUpdateModal(false); setInlineUpdates(null);}}>
        <Modal.Header closeButton style={{background:"linear-gradient(180deg, var(--muted), #f7fbff)"}}>
          <Modal.Title>{inlineUpdates?.isPlan?"Update Subscription Plan":"Update Status"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
              <h5>Are you sure to update {inlineUpdates?.isPlan?`Subscription Plan to ${inlineUpdates?.changedValue}`:`Status to ${inlineUpdates?.changedValue}`}?</h5>

              <div className='d-flex gap-3 mt-4 justify-content-end'>
                    <Button variant="secondary" onClick={() => {setInlineUpdateModal(false)
                      setInlineUpdates(null)
                    }}>
                      Cancel
                    </Button>
                      <Button type="button" variant={"primary"} onClick={() => handleUpdateStatusOrPlan()}>
                        Update
                      </Button>
                  </div>
        </Modal.Body>
      </Modal>


      <Modal size='md' show={deleteContactUsModal} onHide={() => {setDeleteContactUsModal(false); setDeleteContactUsDetails(null);}}>
        <Modal.Header closeButton style={{background:"linear-gradient(180deg, var(--muted), #f7fbff)"}}>
          <Modal.Title>Delete User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
              <h5>Are you sure you want to delete this Contacted user - {deleteContactUsDetails?.name}?</h5>

              <div className='d-flex gap-3 mt-3 justify-content-end'>
                    <Button variant="secondary" onClick={() => {setDeleteContactUsModal(false); setDeleteContactUsDetails(null);}}>
                      Cancel
                    </Button>
                      <Button type="button" variant="danger" onClick={() => handleDeleteContactUs(deleteContactUsDetails)}>
                        Delete
                      </Button>
                  </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default AdminUI;
